import React, { useEffect, useState } from "react";
import { Table, Button, Badge, Pagination, Dropdown } from "react-bootstrap";
import { ClipLoader } from "react-spinners";
import moment from "moment";
import {
  getWebhookDeliveries,
  retryWebhookDelivery,
  testWebhook,
} from "src/docuclipper/api";
import { useDispatch } from "react-redux";
import { createAlert } from "../../../redux/reducers/Alerts";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface Delivery {
  id: string;
  webhookId: string;
  status: "pending" | "success" | "failed";
  responseCode?: number;
  responseBody?: string;
  createdAt: string;
  event: string;
  payload: any;
}

const WebhookDeliveries: React.FC = () => {
  const location = useLocation();
  const webhookId = new URLSearchParams(location.search).get("webhookId");
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState<
    "pending" | "success" | "failed" | undefined
  >();
  const [testingWebhook, setTestingWebhook] = useState<string | null>(null);
  const dispatch = useDispatch();

  const fetchDeliveries = async () => {
    if (!webhookId) return;

    setLoading(true);
    try {
      const response = await getWebhookDeliveries({
        webhookId,
        limit: 10,
        offset: (page - 1) * 10,
        status,
      });
      setDeliveries(response.deliveries);
      setTotalPages(Math.ceil(response.total / 10));
    } catch (error) {
      dispatch(
        createAlert({
          message: "Failed to fetch delivery logs",
          type: "error",
          timeout: 0,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveries();
  }, [webhookId, page, status]);

  const handleRetry = async (deliveryId: string) => {
    try {
      await retryWebhookDelivery(deliveryId);
      dispatch(
        createAlert({
          message: "Delivery retry initiated",
          type: "success",
          timeout: 5000,
        })
      );
      fetchDeliveries();
    } catch (error) {
      dispatch(
        createAlert({
          message: "Failed to retry delivery",
          type: "error",
          timeout: 0,
        })
      );
    }
  };

  const handleTest = async (eventType: "bank" | "invoice") => {
    if (!webhookId) return;

    setTestingWebhook(webhookId);
    try {
      await testWebhook(webhookId, eventType);
      dispatch(
        createAlert({
          message: "Test webhook sent successfully",
          type: "success",
          timeout: 5000,
        })
      );
      fetchDeliveries();
    } catch (error) {
      dispatch(
        createAlert({
          message: "Failed to send test webhook",
          type: "error",
          timeout: 0,
        })
      );
    } finally {
      setTestingWebhook(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: "warning",
      success: "success",
      failed: "danger",
    };
    return (
      <Badge variant={variants[status as keyof typeof variants]}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <ClipLoader color="#0d6efd" size={50} />
      </div>
    );
  }

  return (
    <div style={{ textAlign: "left" }} className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2">
          <Button
            variant={status === undefined ? "primary" : "outline-primary"}
            onClick={() => setStatus(undefined)}
            className="mr-1"
          >
            All
          </Button>
          <Button
            variant={status === "pending" ? "primary" : "outline-primary"}
            onClick={() => setStatus("pending")}
            className="mr-1"
          >
            Pending
          </Button>
          <Button
            variant={status === "success" ? "primary" : "outline-primary"}
            onClick={() => setStatus("success")}
            className="mr-1"
          >
            Success
          </Button>
          <Button
            variant={status === "failed" ? "primary" : "outline-primary"}
            onClick={() => setStatus("failed")}
            className="mr-1"
          >
            Failed
          </Button>
        </div>
        <Dropdown>
          <Dropdown.Toggle variant="outline-primary" size="sm">
            <FontAwesomeIcon icon="cog" className="mr-1" />
            Test Webhook
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item
              onClick={() => handleTest("bank")}
              disabled={!!testingWebhook}
            >
              {testingWebhook === webhookId ? (
                <ClipLoader color="#000000" size={12} />
              ) : (
                "Send Test Webhook (bank data)"
              )}
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => handleTest("invoice")}
              disabled={!!testingWebhook}
            >
              {testingWebhook === webhookId ? (
                <ClipLoader color="#000000" size={12} />
              ) : (
                "Send Test Webhook (invoice data)"
              )}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <Table responsive hover style={{ marginLeft: 0 }}>
        <thead>
          <tr>
            {/* <th>Event</th> */}
            <th>Status</th>
            <th>Response</th>
            <th>Created</th>
            {/* <th>Actions</th> */}
          </tr>
        </thead>
        <tbody>
          {deliveries.map((delivery) => (
            <tr key={delivery.id}>
              {/* <td>{delivery.event}</td> */}
              <td>{getStatusBadge(delivery.status)}</td>
              <td>
                {delivery.responseCode && (
                  <div>
                    <Badge variant="secondary">{delivery.responseCode}</Badge>
                    {delivery.responseBody && (
                      <div
                        className="text-truncate"
                        style={{ maxWidth: "200px" }}
                      >
                        {delivery.responseBody}
                      </div>
                    )}
                  </div>
                )}
              </td>
              <td>
                {moment(delivery.createdAt).format("MMM D, YYYY HH:mm:ss")}
              </td>
              {/* <td>
                {delivery.status === "failed" && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleRetry(delivery.id)}
                  >
                    Retry
                  </Button>
                )}
              </td> */}
            </tr>
          ))}
        </tbody>
      </Table>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.Prev
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            />
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (pageNum) => (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === page}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </Pagination.Item>
              )
            )}
            <Pagination.Next
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            />
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default WebhookDeliveries;
